// src/pages/Events/EnterpriseEventsWorkspace.jsx
// 🏢 FIXED: Removed conflicting controls, fixed bookmarks, downloads, and multi-camera

import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  FiPlay,
  FiPause,
  FiSkipBack,
  FiSkipForward,
  FiDownload,
  FiCamera,
  FiFilter,
  FiBookmark,
  FiGrid,
  FiMaximize2,
  FiX,
  FiMenu,
  FiChevronUp,
} from "react-icons/fi";
import authAxios from "../../api/authAxios";
import ProfessionalTimeline from "./components/ProfessionalTimeline";
import VideoPlayer from "./components/VideoPlayer";
import SmartSearchPanel from "./components/SmartSearchPanel";
import BookmarkManager from "./components/BookmarkManager";
import ExportDialog from "./components/ExportDialog";
import MultiCameraView from "./components/MultiCameraView";

export default function EnterpriseEventsWorkspace({ cameras = [] }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // ==========================================
  // STATE
  // ==========================================

  // View mode
  const [viewMode, setViewMode] = useState("single");
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [focusedCamera, setFocusedCamera] = useState(null);

  // Mobile UI
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileBookmarks, setShowMobileBookmarks] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(true);

  // Timeline & Playback
  const [recordings, setRecordings] = useState([]);
  const [multiCameraRecordings, setMultiCameraRecordings] = useState({}); // For multi-camera view
  const [currentTime, setCurrentTime] = useState(new Date());
  const [playing, setPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [timelineZoom, setTimelineZoom] = useState(1);

  // Search & Filters
  const [searchDate, setSearchDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [showSearchPanel, setShowSearchPanel] = useState(true);

  // Bookmarks
  const [bookmarks, setBookmarks] = useState(() => {
    const saved = localStorage.getItem("viewmax-bookmarks");
    return saved ? JSON.parse(saved) : [];
  });
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Export
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedClip, setSelectedClip] = useState(null);

  // Analytics
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [detectionOverlays, setDetectionOverlays] = useState([]);

  // Loading
  const [loading, setLoading] = useState(false);

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ==========================================
  // FUNCTIONS
  // ==========================================

  const loadRecordings = async () => {
    if (!focusedCamera) return;
    setLoading(true);
    try {
      const res = await authAxios.get("/recordings/segments/search/", {
        params: {
          camera_id: focusedCamera.id,
          start_time: `${searchDate}T${startTime}`,
          end_time: `${searchDate}T${endTime}`,
        },
      });
      setRecordings(res.data.results || []);
    } catch (err) {
      console.error("Failed to load recordings:", err);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Load recordings for all selected cameras (multi-camera view)
  const loadMultiCameraRecordings = async () => {
    if (selectedCameras.length === 0) return;
    
    const recordingsMap = {};
    await Promise.all(
      selectedCameras.map(async (camera) => {
        try {
          const res = await authAxios.get("/recordings/segments/search/", {
            params: {
              camera_id: camera.id,
              start_time: `${searchDate}T${startTime}`,
              end_time: `${searchDate}T${endTime}`,
            },
          });
          recordingsMap[camera.id] = res.data.results || [];
        } catch (err) {
          console.error(`Failed to load recordings for camera ${camera.id}:`, err);
          recordingsMap[camera.id] = [];
        }
      })
    );
    setMultiCameraRecordings(recordingsMap);
  };

  useEffect(() => {
    if (focusedCamera) {
      loadRecordings();
    }
  }, [focusedCamera, searchDate, startTime, endTime]);

  // ✅ NEW: Load multi-camera recordings when in multi view
  useEffect(() => {
    if (viewMode === "multi" && selectedCameras.length > 0) {
      loadMultiCameraRecordings();
    }
  }, [viewMode, selectedCameras, searchDate, startTime, endTime]);

  const loadEvents = async () => {
    if (!focusedCamera) return;
    try {
      const res = await authAxios.get("/analytics/detections/", {
        params: { camera_id: focusedCamera.id, date: searchDate },
      });
      setDetectionOverlays(res.data.results || []);
    } catch (err) {
      console.error("Failed to load events:", err);
      setDetectionOverlays([]);
    }
  };

  useEffect(() => {
    if (focusedCamera && showAnalytics) {
      loadEvents();
    }
  }, [focusedCamera, searchDate, showAnalytics]);

  const handlePlay = () => setPlaying(true);
  const handlePause = () => setPlaying(false);

  const addBookmark = async (note = "") => {
    const bookmark = {
      id: Date.now(),
      camera_id: focusedCamera.id,
      camera_name: focusedCamera.name,
      timestamp: currentTime.toISOString(),
      note,
      created_at: new Date().toISOString(),
    };
    const newBookmarks = [...bookmarks, bookmark];
    setBookmarks(newBookmarks);
    localStorage.setItem("viewmax-bookmarks", JSON.stringify(newBookmarks));
    try {
      await authAxios.post("/recordings/bookmarks/", {
        camera_id: focusedCamera.id,
        timestamp: currentTime.toISOString(),
        note,
      });
    } catch (err) {
      console.log("ℹ️ Bookmark saved locally");
    }
  };

  const deleteBookmark = (id) => {
    const newBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(newBookmarks);
    localStorage.setItem("viewmax-bookmarks", JSON.stringify(newBookmarks));
    authAxios.delete(`/recordings/bookmarks/${id}/`).catch(() => {});
  };

  const editBookmark = (id, note) => {
    const newBookmarks = bookmarks.map(b => b.id === id ? { ...b, note } : b);
    setBookmarks(newBookmarks);
    localStorage.setItem("viewmax-bookmarks", JSON.stringify(newBookmarks));
  };

  const jumpToBookmark = (bookmark) => {
    setCurrentTime(new Date(bookmark.timestamp));
    if (bookmark.camera_id !== focusedCamera?.id) {
      const camera = cameras.find(c => c.id === bookmark.camera_id);
      if (camera) {
        setFocusedCamera(camera);
        if (!selectedCameras.find(c => c.id === camera.id)) {
          setSelectedCameras([...selectedCameras, camera]);
        }
      }
    }
  };

  const cameraBookmarks = focusedCamera
    ? bookmarks.filter(b => b.camera_id === focusedCamera.id)
    : bookmarks;

  // ✅ FIXED: Actual download functionality
  const handleQuickDownload = () => {
    if (!focusedCamera || recordings.length === 0) return;
    
    // Find current recording
    const currentRecording = recordings.find(rec => {
      const start = new Date(rec.start_time);
      const end = new Date(rec.end_time);
      return currentTime >= start && currentTime <= end;
    });

    if (currentRecording) {
      // Direct download
      const link = document.createElement('a');
      link.href = currentRecording.file_url;
      link.download = `${focusedCamera.name}_${new Date(currentRecording.start_time).toISOString().slice(0, 19).replace(/:/g, '-')}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("No recording found for current time");
    }
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let start, end;
    switch (preset) {
      case "last-hour":
        start = new Date(now.getTime() - 60 * 60 * 1000);
        end = now;
        break;
      case "last-4h":
        start = new Date(now.getTime() - 4 * 60 * 60 * 1000);
        end = now;
        break;
      case "today":
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      default:
        return;
    }
    setSearchDate(start.toISOString().slice(0, 10));
    setStartTime(start.toTimeString().slice(0, 5));
    setEndTime(end.toTimeString().slice(0, 5));
  };

  const cardClass = isDark
    ? "bg-neutral-900 border-neutral-800"
    : "bg-white border-gray-200";

  // ✅ FIXED: Multi-camera view toggle
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    if (mode === "multi" && selectedCameras.length === 0 && focusedCamera) {
      // Auto-select focused camera if nothing selected
      setSelectedCameras([focusedCamera]);
    }
  };

  // ==========================================
  // MOBILE LAYOUT
  // ==========================================

  if (isMobile) {
    return (
      <div className="h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-neutral-900">
        {/* Mobile Header */}
        <header className={`${cardClass} border-b px-4 py-3 flex items-center justify-between safe-area-top`}>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
            >
              <FiMenu />
            </button>
            <h1 className="text-lg font-bold">Events</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMobileBookmarks(!showMobileBookmarks)}
              className={`p-2 rounded ${showMobileBookmarks ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-neutral-700"}`}
            >
              <FiBookmark />
            </button>
            <button
              onClick={handleQuickDownload}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-neutral-700"
              disabled={!focusedCamera}
            >
              <FiDownload />
            </button>
          </div>
        </header>

        {/* Mobile Video */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!focusedCamera ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 p-4">
              <div className="text-center">
                <FiCamera className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Select a camera to view recordings</p>
              </div>
            </div>
          ) : (
            <VideoPlayer
              camera={focusedCamera}
              recordings={recordings}
              currentTime={currentTime}
              setCurrentTime={setCurrentTime}
              playing={playing}
              playbackSpeed={playbackSpeed}
              showAnalytics={showAnalytics}
              detectionOverlays={detectionOverlays}
              onAddBookmark={addBookmark}
            />
          )}

          {/* Mobile Timeline */}
          {focusedCamera && (
            <div className={`border-t ${cardClass}`}>
              <ProfessionalTimeline
                camera={focusedCamera}
                recordings={recordings}
                events={detectionOverlays}
                bookmarks={cameraBookmarks}
                currentTime={currentTime}
                setCurrentTime={setCurrentTime}
                zoom={timelineZoom}
                setZoom={setTimelineZoom}
                date={searchDate}
                playing={playing}
                compact={true}
              />
            </div>
          )}
        </div>

        {/* Mobile Filter Panel */}
        {showMobileFilters && (
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowMobileFilters(false)}>
            <div
              className={`${cardClass} h-full w-80 max-w-[85vw] overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-inherit border-b px-4 py-3 flex items-center justify-between">
                <h2 className="font-bold">Filters</h2>
                <button onClick={() => setShowMobileFilters(false)}>
                  <FiX />
                </button>
              </div>
              <SmartSearchPanel
                cameras={cameras}
                selectedCameras={selectedCameras}
                onSelectCamera={(cam) => {
                  setFocusedCamera(cam);
                  if (!selectedCameras.find(c => c.id === cam.id)) {
                    setSelectedCameras([...selectedCameras, cam]);
                  }
                  setShowMobileFilters(false);
                }}
                searchDate={searchDate}
                setSearchDate={setSearchDate}
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                onApplyPreset={applyPreset}
                eventTypeFilter={eventTypeFilter}
                setEventTypeFilter={setEventTypeFilter}
              />
            </div>
          </div>
        )}

        {/* Mobile Bookmark Panel - FIXED POSITIONING */}
        {showMobileBookmarks && (
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowMobileBookmarks(false)}>
            <div
              className={`${cardClass} h-full w-80 max-w-[85vw] ml-auto overflow-y-auto`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-inherit border-b px-4 py-3 flex items-center justify-between">
                <h2 className="font-bold">Bookmarks</h2>
                <button onClick={() => setShowMobileBookmarks(false)}>
                  <FiX />
                </button>
              </div>
              <BookmarkManager
                bookmarks={cameraBookmarks}
                onJumpTo={(bookmark) => {
                  jumpToBookmark(bookmark);
                  setShowMobileBookmarks(false);
                }}
                onDelete={deleteBookmark}
                onEdit={editBookmark}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================
  // DESKTOP LAYOUT
  // ==========================================

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Desktop Header */}
      <header className={`border-b ${cardClass} px-6 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Recording Manager</h1>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FiCamera className="w-4 h-4" />
            <span>{cameras.length} cameras</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewModeChange("single")}
            className={`p-2 rounded ${
              viewMode === "single" ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            title="Single camera view"
          >
            <FiMaximize2 />
          </button>
          <button
            onClick={() => handleViewModeChange("multi")}
            className={`p-2 rounded ${
              viewMode === "multi" ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            title="Multi-camera view"
            disabled={selectedCameras.length === 0}
          >
            <FiGrid />
          </button>
          <button
            onClick={() => setShowBookmarks(!showBookmarks)}
            className={`p-2 rounded ${
              showBookmarks ? "bg-blue-600 text-white" : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            title="Show bookmarks"
          >
            <FiBookmark />
          </button>
          <button
            onClick={handleQuickDownload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={!focusedCamera}
            title="Download current recording"
          >
            <FiDownload />
            Download
          </button>
        </div>
      </header>

      {/* Desktop Content */}
      <div className="flex-1 flex overflow-hidden">
        {showSearchPanel && (
          <aside className={`w-80 border-r ${cardClass} overflow-y-auto`}>
            <SmartSearchPanel
              cameras={cameras}
              selectedCameras={selectedCameras}
              onSelectCamera={(cam) => {
                setFocusedCamera(cam);
                if (!selectedCameras.find(c => c.id === cam.id)) {
                  setSelectedCameras([...selectedCameras, cam]);
                }
              }}
              searchDate={searchDate}
              setSearchDate={setSearchDate}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              onApplyPreset={applyPreset}
              eventTypeFilter={eventTypeFilter}
              setEventTypeFilter={setEventTypeFilter}
            />
          </aside>
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          {!focusedCamera ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <FiCamera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Select a camera to view recordings</p>
              </div>
            </div>
          ) : viewMode === "single" ? (
            <VideoPlayer
              camera={focusedCamera}
              recordings={recordings}
              currentTime={currentTime}
              setCurrentTime={setCurrentTime}
              playing={playing}
              playbackSpeed={playbackSpeed}
              showAnalytics={showAnalytics}
              detectionOverlays={detectionOverlays}
              onAddBookmark={addBookmark}
            />
          ) : (
            <MultiCameraView
              cameras={selectedCameras}
              currentTime={currentTime}
              recordings={multiCameraRecordings}
              playing={playing}
            />
          )}

          {/* Timeline - ALWAYS SHOW */}
          {focusedCamera && (
            <div className={`border-t ${cardClass}`}>
              <ProfessionalTimeline
                camera={focusedCamera}
                recordings={recordings}
                events={detectionOverlays}
                bookmarks={cameraBookmarks}
                currentTime={currentTime}
                setCurrentTime={setCurrentTime}
                zoom={timelineZoom}
                setZoom={setTimelineZoom}
                date={searchDate}
                playing={playing}
              />
            </div>
          )}

          {/* ✅ REMOVED: Desktop Playback Controls - Video has native controls */}
        </div>

        {/* Bookmark Panel - FIXED POSITIONING */}
        {showBookmarks && (
          <aside className={`w-80 border-l ${cardClass} overflow-y-auto`}>
            <BookmarkManager
              bookmarks={cameraBookmarks}
              onJumpTo={jumpToBookmark}
              onDelete={deleteBookmark}
              onEdit={editBookmark}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
